import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { IntegrationCatalog } from "@modules/erpIntegrations/types";
import { useCreateConfigMutation } from "@modules/erpIntegrations/services/mutations";
import { listConfigsQueryKey } from "@modules/erpIntegrations/services/queries";
import { applyConfigApiErrors } from "@modules/erpIntegrations/utils";
import {
  ConfigFormValues,
  configSchema,
} from "@modules/erpIntegrations/validations";

export function useCreateConfigModal(
  modalRef: RefObject<ModalRef>,
  integrations: IntegrationCatalog[],
  onComplete?: () => void
) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<ConfigFormValues>({
    resolver: zodResolver(configSchema(integrations)),
    mode: "onBlur",
    defaultValues: {
      active: true,
      credentials: {},
      test_document: "",
      access_token: "",
      plan_map_option: null,
    },
  });

  const { mutateAsync, isPending } = useCreateConfigMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listConfigsQueryKey,
        refetchType: "all",
      });

      open?.({
        type: "success",
        message: translate("v1/agreement_integration_config_admin.success.create"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
    onError: (error) => {
      applyConfigApiErrors(error, methods.setError);
    },
  });

  return {
    methods,
    mutateAsync,
    isPending,
  };
}
